import { useState, useEffect, useRef } from 'react';
import { configService } from '@/services/configService';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import type { FeedbackCollectorParams } from '@/types/feedbackCollector';

/**
 * Custom hook to build the feedback collector component parameters.
 *
 * Sources userId from TimesheetContext, customerName from CompanyContext,
 * and apiHost / accountToken / product from the feedback collector config.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Homepage", "Timesheet")
 * @returns The EvoMfeApp componentParams object, or null if config is not loaded yet
 */
export function useFeedbackCollectorParams(feature: string): FeedbackCollectorParams | null {
  const [params, setParams] = useState<FeedbackCollectorParams | null>(null);
  const { currentEmployee } = useTimesheet();
  const { currentCompany } = useCompany();

  // Derive only the primitive values we actually use so that unrelated
  // context re-renders (e.g. timesheet week navigation updating entries)
  // don't re-trigger the effect and cause the MFE to remount.
  const employeeId = currentEmployee?.id;
  const employeeName = currentEmployee?.name;
  const companyName = currentCompany?.name;

  // Keep a ref of the last serialised params to avoid setting identical state
  // (which would produce a new object reference and cascade re-renders).
  const prevSerialised = useRef<string>('');

  useEffect(() => {
    let isMounted = true;

    async function buildParams() {
      try {
        const config = await configService.getFeedbackCollectorConfig();

        if (!isMounted) return;

        const next: FeedbackCollectorParams = {
          feature,
          accountToken: config.accountToken,
          apiHost: config.apiHost,
          customerName: companyName || 'Unknown',
          userId: employeeId !== undefined ? String(employeeId) : employeeName || 'Unknown',
          product: config.product,
        };

        const serialised = JSON.stringify(next);
        if (serialised !== prevSerialised.current) {
          prevSerialised.current = serialised;
          setParams(next);
        }
      } catch (error) {
        console.error('Failed to build feedback collector params:', error);
        setParams(null);
      }
    }

    buildParams();

    return () => {
      isMounted = false;
    };
  }, [feature, employeeId, employeeName, companyName]);

  return params;
}
